import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ToolsAPI, ClientToolsAPI } from "../api/tools";
import type { ClientTool } from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";

export const toolKeys = {
  all: ["tools"] as const,
  lists: () => [...toolKeys.all, "list"] as const,
  list: (clientId: string) => [...toolKeys.lists(), clientId] as const,
  detail: (clientId: string, id: string) =>
    [...toolKeys.all, "detail", clientId, id] as const,
};

export const clientToolKeys = {
  all: ["client-tools"] as const,
  lists: () => [...clientToolKeys.all, "list"] as const,
  list: (clientId: string) => [...clientToolKeys.lists(), clientId] as const,
  available: (clientId: string) =>
    [...clientToolKeys.lists(), clientId, "available"] as const,
  detail: (clientId: string, id: string) =>
    [...clientToolKeys.all, "detail", clientId, id] as const,
};

/**
 * Get all available tool definitions
 */
export function useTools() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery({
    queryKey: toolKeys.list(clientId),
    queryFn: async () => {
      const data = await ToolsAPI.list(clientId);
      return data.tools;
    },
    enabled: !!clientId,
  });
}

/**
 * Get a specific tool definition
 */
export function useTool(id: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery({
    queryKey: toolKeys.detail(clientId, id),
    queryFn: async () => {
      const data = await ToolsAPI.get(clientId, id);
      return data.tool;
    },
    enabled: !!clientId && !!id,
  });
}

/**
 * Get all activated tools for the client
 */
export function useClientTools() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery({
    queryKey: clientToolKeys.list(clientId),
    queryFn: async () => {
      const data = await ClientToolsAPI.list(clientId);
      return data.clientTools;
    },
    enabled: !!clientId,
  });
}

/**
 * Get all available tools (activated and not activated)
 */
export function useAvailableTools() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery({
    queryKey: clientToolKeys.available(clientId),
    queryFn: async () => {
      const data = await ClientToolsAPI.listAvailable(clientId);
      return data.available;
    },
    enabled: !!clientId,
  });
}

/**
 * Get a specific client tool configuration
 */
export function useClientTool(id: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery({
    queryKey: clientToolKeys.detail(clientId, id),
    queryFn: async () => {
      const data = await ClientToolsAPI.get(clientId, id);
      return data.clientTool;
    },
    enabled: !!clientId && !!id,
  });
}

/**
 * Activate a tool for the client
 */
export function useCreateClientTool() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ClientTool>) =>
      ClientToolsAPI.create(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.available(clientId),
      });
    },
  });
}

/**
 * Update a client tool configuration
 */
export function useUpdateClientTool() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClientTool> }) =>
      ClientToolsAPI.update(clientId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.available(clientId),
      });
    },
  });
}

/**
 * Delete a client tool (deactivate)
 */
export function useDeleteClientTool() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ClientToolsAPI.delete(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.available(clientId),
      });
    },
  });
}

/**
 * Toggle a tool enabled/disabled state
 */
export function useToggleClientTool() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      ClientToolsAPI.toggle(clientId, id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.list(clientId),
      });
    },
  });
}

/**
 * Update per-agent configuration
 */
export function useUpdateAgentConfiguration() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      agentId,
      agentConfig,
    }: {
      id: string;
      agentId: string;
      agentConfig: {
        enabled?: boolean;
        configuration?: Record<string, unknown>;
      };
    }) =>
      ClientToolsAPI.updateAgentConfiguration(
        clientId,
        id,
        agentId,
        agentConfig,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.available(clientId),
      });
    },
  });
}

/**
 * Delete per-agent configuration
 */
export function useDeleteAgentConfiguration() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, agentId }: { id: string; agentId: string }) =>
      ClientToolsAPI.deleteAgentConfiguration(clientId, id, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: clientToolKeys.available(clientId),
      });
    },
  });
}
